import { BarChart, EditOutlined, MergeOutlined } from '@mui/icons-material';
import {
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';

import Avatar from '@/components/avatar/Avatar';
import Card from '@/components/card';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Creator } from '@/models/creator';
import shadows from '@/themes/theme/shadows';
import { getPrettyDate } from '@/utils/datesAndTime';
import getRoute from '@/utils/getRoute';

interface Props {
  creator: Creator.Details | undefined;
  showSkeleton: boolean;
  onClickEdit: () => void;
  onClickMerge: () => void;
}

const ProfileSkeleton = () => (
  <Stack direction="row" justifyContent="space-between">
    <Stack direction="row" alignItems="center" gap={4}>
      <Skeleton variant="circular" width={170} height={170} />
      <Stack justifyContent="space-between">
        <Skeleton variant="text" width="200px" sx={{ fontSize: '3rem' }} />
      </Stack>
    </Stack>
    <Stack alignItems="flex-end" justifyContent="space-between">
      <Stack alignItems="center" direction="row">
        <IconButton disabled>
          <MergeOutlined />
        </IconButton>
        <IconButton disabled>
          <EditOutlined />
        </IconButton>
      </Stack>
      <Card hasBorder content={<Skeleton variant="text" width={200} />} />
    </Stack>
  </Stack>
);

const Profile = ({
  creator,
  showSkeleton,
  onClickEdit,
  onClickMerge
}: Props) => {
  const theme = useTheme();
  const navigate = useWorkspaceNavigate();
  const { avatarUrl, id, name, creationDate, violationCount, groups } =
    creator ?? ({} as Creator.Details);

  const TOOLTIP_SLOT_PROPS = {
    tooltip: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        fontSize: 14,
        fontWeight: 400,
        boxShadow: shadows.cardDefault
      }
    },
    arrow: {
      sx: {
        '&::before': {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`
        }
      }
    }
  };

  const violationExists = violationCount > 0;

  const handleViolationClick = () => {
    if (!violationExists) return;
    navigate(
      `${getRoute.violations.LIST()}?status=open%2Cother%2Cclosed&page=1&creator=${id}`
    );
  };

  if (showSkeleton) {
    return (
      <Card
        sx={{
          marginBottom: '16px',
          boxShadow: '0px 6px 18px rgba(0, 0, 0, 0.06)'
        }}
        content={<ProfileSkeleton />}
      />
    );
  }

  return (
    <Card
      sx={{
        marginBottom: '16px',
        boxShadow: '0px 6px 18px rgba(0, 0, 0, 0.06)'
      }}
      content={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={4}>
            <Avatar
              src={avatarUrl ?? ''}
              name={name}
              width="170px"
              fontSize={68}
            />
            <Stack>
              <Typography variant="h4">{name}</Typography>
            </Stack>
          </Stack>
          <Stack alignItems="flex-end" justifyContent="space-between">
            <Stack alignItems="center" direction="row">
              <Tooltip title="Statistics dashboard">
                <IconButton
                  id="creators-statistics"
                  onClick={() =>
                    navigate(
                      `${getRoute.dashboard.WORKSPACE()}?creatorIds=${id}`
                    )
                  }
                >
                  <BarChart />
                </IconButton>
              </Tooltip>
              <Tooltip title="Merge creator">
                <IconButton id="creators-merge" onClick={onClickMerge}>
                  <MergeOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit creator">
                <IconButton id="creators-edit" onClick={onClickEdit}>
                  <EditOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
            <Card
              hasBorder
              content={
                <Stack gap={1}>
                  <Typography fontWeight={300}>
                    {'Creator created at: '}
                    <Typography component="span" fontWeight={400}>
                      {getPrettyDate(creationDate)}
                    </Typography>
                  </Typography>
                  <Typography fontWeight={300}>
                    {'Violations: '}
                    <Typography
                      component="span"
                      fontWeight={400}
                      onClick={handleViolationClick}
                      sx={{
                        cursor: violationExists ? 'pointer' : 'default',
                        textDecoration: violationExists ? 'underline' : 'none'
                      }}
                    >
                      {violationCount}
                    </Typography>
                  </Typography>
                  <Tooltip
                    title={groups?.join(', ')}
                    arrow
                    slotProps={TOOLTIP_SLOT_PROPS}
                  >
                    <Typography
                      fontWeight={300}
                      sx={{
                        maxWidth: 350,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'default'
                      }}
                    >
                      {'Creator Groups: '}
                      <Typography component="span" fontWeight={400}>
                        {groups?.join(', ')}
                      </Typography>
                    </Typography>
                  </Tooltip>
                </Stack>
              }
            />
          </Stack>
        </Stack>
      }
    />
  );
};

export default Profile;
